import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'row', 'list']

  connect() {
    // Initialize filter on connect
    this.filter()
    // Set up observer to focus input when menu becomes visible
    this.setupVisibilityObserver()
    // Set up keyboard navigation
    this.setupKeyboardNavigation()
    this.currentIndex = -1
    // Calculate column count based on CSS
    this.updateColumnCount()
    // Update column count on window resize
    this.boundUpdateColumnCount = this.updateColumnCount.bind(this)
    window.addEventListener('resize', this.boundUpdateColumnCount)
  }

  disconnect() {
    // Clean up observer when controller disconnects
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
    // Clean up keyboard event listeners
    if (this.boundHandleKeydown) {
      this.element.removeEventListener('keydown', this.boundHandleKeydown)
    }
    // Clean up resize listener
    if (this.boundUpdateColumnCount) {
      window.removeEventListener('resize', this.boundUpdateColumnCount)
    }
  }

  setupVisibilityObserver() {
    // Find the closest parent that might be hidden/shown (the toggleable container)
    let parent = this.element.parentElement
    while (parent && !parent.classList.contains('fixed')) {
      parent = parent.parentElement
    }
    
    if (!parent || !this.hasInputTarget) return

    // Watch for changes to the parent's class list
    this.visibilityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isVisible = !parent.classList.contains('hidden') && 
                           !parent.classList.contains('opacity-0')
          if (isVisible) {
            // Small delay to ensure transition has started and input is ready
            setTimeout(() => {
              if (this.hasInputTarget) {
                this.inputTarget.focus()
              }
            }, 150)
          }
        }
      })
    })

    // Start observing
    this.visibilityObserver.observe(parent, {
      attributes: true,
      attributeFilter: ['class']
    })

    // Also check immediately if already visible
    if (!parent.classList.contains('hidden') && !parent.classList.contains('opacity-0')) {
      setTimeout(() => {
        if (this.hasInputTarget) {
          this.inputTarget.focus()
        }
      }, 150)
    }
  }

  filter() {
    const searchTerm = this.inputTarget.value.toLowerCase().trim()
    
    // Only filter if search term has at least one character
    if (searchTerm.length < 1) {
      this.rowTargets.forEach(row => {
        row.classList.remove('hidden')
      })
      return
    }

    this.rowTargets.forEach(row => {
      const text = row.textContent.toLowerCase()
      if (text.includes(searchTerm)) {
        row.classList.remove('hidden')
      } else {
        row.classList.add('hidden')
      }
    })
    
    // Reset navigation index when filtering
    this.currentIndex = -1
  }

  setupKeyboardNavigation() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.element.addEventListener('keydown', this.boundHandleKeydown)
  }

  handleKeydown(event) {
    // Get all visible branch links
    const visibleLinks = this.getVisibleLinks()
    
    if (visibleLinks.length === 0) return

    const isInputFocused = document.activeElement === this.inputTarget
    const isLinkFocused = visibleLinks.includes(document.activeElement)

    // Handle TAB key - move to first branch if coming from input
    if (event.key === 'Tab' && !event.shiftKey && isInputFocused) {
      event.preventDefault()
      this.focusLink(visibleLinks[0])
      this.currentIndex = 0
      return
    }

    // Handle arrow keys - navigate through branches (2D navigation)
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      // Only handle if input is focused or a link is focused
      if (isInputFocused || isLinkFocused) {
        event.preventDefault()
        
        if (this.currentIndex === -1 || isInputFocused) {
          // Start navigation from first visible link
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            this.currentIndex = 0
          } else {
            this.currentIndex = visibleLinks.length - 1
          }
        } else {
          // 2D navigation
          this.currentIndex = this.navigate2D(event.key, this.currentIndex, visibleLinks.length)
        }
        
        this.focusLink(visibleLinks[this.currentIndex])
      }
      return
    }

    // Handle ENTER key - activate selected link
    if (event.key === 'Enter') {
      if (isLinkFocused) {
        // If a link is focused, click it
        event.preventDefault()
        document.activeElement.click()
        return
      } else if (this.currentIndex >= 0 && this.currentIndex < visibleLinks.length) {
        // If we have a tracked index, click that link
        event.preventDefault()
        visibleLinks[this.currentIndex].click()
        return
      }
    }

    // Handle TAB navigation between branches - track current index
    if (event.key === 'Tab') {
      const focusedLink = visibleLinks.find(link => link === document.activeElement)
      if (focusedLink) {
        const currentFocusedIndex = visibleLinks.indexOf(focusedLink)
        this.currentIndex = currentFocusedIndex
      }
    }
  }

  getVisibleLinks() {
    // Get all links that are in visible rows (not hidden)
    return Array.from(this.element.querySelectorAll('a[tabindex="0"]')).filter(link => {
      const row = link.closest('li[data-filter-target="row"]')
      return row && !row.classList.contains('hidden')
    })
  }

  focusLink(link) {
    if (link) {
      link.focus()
      // Scroll into view if needed
      link.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  updateColumnCount() {
    // Get the computed column count from CSS
    if (this.hasListTarget) {
      const computedStyle = window.getComputedStyle(this.listTarget)
      // Try different ways to get column count
      let columnCount = computedStyle.columnCount
      if (!columnCount || columnCount === 'auto') {
        columnCount = computedStyle.webkitColumnCount || computedStyle.mozColumnCount
      }
      columnCount = parseInt(columnCount) || null
      
      // Fallback: use media query logic (matches CSS: 2 columns on mobile, 3 on desktop)
      if (!columnCount || columnCount <= 0) {
        this.columnCount = window.innerWidth >= 768 ? 3 : 2
      } else {
        this.columnCount = columnCount
      }
    } else {
      // Fallback: use media query logic
      this.columnCount = window.innerWidth >= 768 ? 3 : 2
    }
  }

  navigate2D(direction, currentIndex, totalLinks) {
    if (totalLinks === 0) return currentIndex
    
    const columnCount = this.columnCount || 2
    const itemsPerColumn = Math.ceil(totalLinks / columnCount)
    
    // CSS columns fill column by column, so visually:
    // Column 0: items 0, 1, 2, ..., itemsPerColumn-1
    // Column 1: items itemsPerColumn, itemsPerColumn+1, ..., 2*itemsPerColumn-1
    // Column 2: items 2*itemsPerColumn, 2*itemsPerColumn+1, ..., 3*itemsPerColumn-1
    // 
    // Visually in the grid:
    // Row 0: item 0, item itemsPerColumn, item 2*itemsPerColumn
    // Row 1: item 1, item itemsPerColumn+1, item 2*itemsPerColumn+1
    // etc.
    
    // Calculate visual position
    const visualCol = Math.floor(currentIndex / itemsPerColumn)
    const visualRow = currentIndex % itemsPerColumn
    
    let newIndex = currentIndex

    switch (direction) {
      case 'ArrowDown':
        // Move down: same column, next row
        // In same column, move to next item in DOM order
        newIndex = currentIndex + 1
        // Check if we're still in the same column
        const newVisualCol = Math.floor(newIndex / itemsPerColumn)
        if (newVisualCol !== visualCol || newIndex >= totalLinks) {
          // Wrap to top of same column
          newIndex = visualCol * itemsPerColumn
          if (newIndex >= totalLinks) {
            newIndex = 0
          }
        }
        break

      case 'ArrowUp':
        // Move up: same column, previous row
        // In same column, move to previous item in DOM order
        newIndex = currentIndex - 1
        const newVisualColUp = Math.floor(newIndex / itemsPerColumn)
        if (newVisualColUp !== visualCol || newIndex < 0) {
          // Wrap to bottom of same column
          const lastRowInCol = Math.min(itemsPerColumn - 1, (totalLinks - 1) % itemsPerColumn)
          newIndex = visualCol * itemsPerColumn + lastRowInCol
          if (newIndex >= totalLinks) {
            newIndex = totalLinks - 1
          }
        }
        break

      case 'ArrowRight':
        // Move right: same row, next column
        // Stay in same visual row, move to next column
        newIndex = visualRow + (visualCol + 1) * itemsPerColumn
        // Check if this position exists
        if (newIndex >= totalLinks) {
          // This column doesn't exist in this row, wrap to first column of next row or top
          if (visualRow + 1 < itemsPerColumn) {
            newIndex = visualRow + 1
          } else {
            // Wrap to top, first column
            newIndex = 0
          }
        }
        break

      case 'ArrowLeft':
        // Move left: same row, previous column
        // Stay in same visual row, move to previous column
        if (visualCol > 0) {
          newIndex = visualRow + (visualCol - 1) * itemsPerColumn
        } else {
          // Already at first column, wrap to last column of previous row or bottom
          if (visualRow > 0) {
            // Go to previous row, last column
            const lastCol = Math.floor((totalLinks - 1) / itemsPerColumn)
            newIndex = (visualRow - 1) + lastCol * itemsPerColumn
            if (newIndex >= totalLinks) {
              newIndex = totalLinks - 1
            }
          } else {
            // Wrap to bottom, last column
            newIndex = totalLinks - 1
          }
        }
        break
    }

    // Ensure index is within bounds
    return Math.max(0, Math.min(newIndex, totalLinks - 1))
  }
}
